import React, { useContext, useEffect, useMemo, useState } from "react";
import CompaniesAutocomplete from "../../../../customer/companies/CompaniesAutocomplete";
import Input from "../../../../../../stories/Input/Input";
import { StyledDatagrid } from "../../../../../../styles/DatagridStyles";
import {
  FlexForColumns,
  HEADER_TEXT_ALIGN,
  SLNO_TEXT_ALIGN,
  TABLE_ROW_HEIGHT,
} from "../../../../../../styles/Variables";
import {
  GridColDef,
  GridColumnVisibilityModel,
  GridValidRowModel,
} from "@mui/x-data-grid-pro";
import { ColumnVisibilityFor } from "../../../../../../utils/Enum.Datagrid";
import eye from "../../../../../../images/EyeWhite.svg";
import { Button } from "../../../../../../stories/Button/Button";
import { CustomDrawer } from "../../../../../../styles/CustomDrawerStyles";
import AssetDetails from "./AssetDetails";
import { AppContext } from "../../../../../../context/context";
import {
  FETCH_MORE_DATA,
  ROWS_PER_PAGE,
  TicketStatus,
  emptyFunc,
} from "../../../../../../utils/constants";
import useLoggedInUserDetails from "../../../../../../customHooks/useLoggedInUserDetails";
import useUrlParams from "../../../../../../customHooks/useUrlParams";
import useLabels from "../../../../../../customHooks/useLabels";
import {
  AtsStckItemDetailsEdges,
  AtsStckItemDetailsOrderField,
  GetItemsDetailsResult,
  GetItemsDetailsVars,
  ItemDetailsQueryType,
} from "../../../masters/items/queries/types";
import { useLazyQuery } from "@apollo/client";
import { GetItemsDetails } from "../../../masters/items/queries/queries";
import { OrderDirection } from "../../../../../../utils/commonQueryTypes";
import { StockEntryTable } from "../../../purchase/stockentry/Add/TableEdit";
import { payloadTypes } from "../../../../../../context/reducer";
import Working from "../../../../../../images/dashboard/Working.svg";
import Notworking from "../../../../../../images/dashboard/Not Working.svg";
import Loading from "../../../../../../images/Loading.gif";
import SubsidiarySelect from "../../../../../../customComponents/SubsidiarySelect";
import SiteSelect from "../../../../../../customComponents/SiteSelect";
import JunctionsAutocomplete from "../../../../../../customComponents/JunctionsAutocomplete";
import TrafficSignalsChart from "../view/GraphWise/TrafficSignalsChart";

interface Props {
  setDetailAssets: React.Dispatch<React.SetStateAction<boolean>>;
}

const AssetsList = ({ setDetailAssets }: Props) => {
  const [assetDetails, setAssetDetails] = useState(false);
  const { state, dispatch } = useContext(AppContext);
  const [searchData, setSearchData] = useState("");
  const [loadingState, setLoadingState] = useState(true);
  const { user_details } = useLoggedInUserDetails();
  const { customer_id } = useUrlParams();
  const { companyLabel, subsidiaryLabel, subsidiarySiteLabel, junctionLabel } =
    useLabels();
  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const [endCursor, setEndCursor] = useState<string | null>(null);
  const [items, setItems] = useState<AtsStckItemDetailsEdges[]>([]);

  const [GetDetails, { data, loading, fetchMore }] = useLazyQuery<
    GetItemsDetailsResult,
    GetItemsDetailsVars
  >(GetItemsDetails);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (customer_id) {
          await GetDetails({
            variables: {
              customerID: customer_id ? customer_id : 0,
              companyID: state.companyId ? state.companyId : 0,
              subsidiaryID: state.subsidiaryId ? state.subsidiaryId : 0,
              junctionID: state.junctionID ? state.junctionID : 0,
              siteID: state.subSiteId ? state.subSiteId : 0,
              input: {
                query_type: ItemDetailsQueryType.DETAILS_BY_ITEM_ID,
                stckCategoryID: 0,
                stckGroupID: 0,
                stckManufacturerID: 0,
                stckUomID: 0,
                stckSubCategoryID: 0,
                itemID: state.itmId ? state.itmId : 0,
                ldgrID: 0,
                poleNum: 0,
                empID: user_details.id!,
              },
              after: null,
              first: ROWS_PER_PAGE,
              name: searchData,
              orderBy: {
                direction: OrderDirection.ASC,
                field: AtsStckItemDetailsOrderField.CREATED_AT,
              },
            },
          });
          setLoadingState(false);
        }
      } catch (err) {
        setLoadingState(false);
        console.error((err as Error).message);
      }
    };
    fetchData();
  }, [
    customer_id,
    GetDetails,
    searchData,
    state.itmId,
    state.companyId,
    state.subsidiaryId,
    state.junctionID,
    state.subSiteId,
  ]);

  const columns: GridColDef[] = [
    {
      headerName: "Sl",
      field: StockEntryTable.SL_No,
      headerAlign: HEADER_TEXT_ALIGN,
      align: SLNO_TEXT_ALIGN,
      hideable: false,
      cellClassName: "number-cell",
    },
    ...(items && items.length && items[0].node.item.trackSerial
      ? [
          {
            headerName: "Serial No.",
            field: StockEntryTable.Serial_No,
            headerAlign: HEADER_TEXT_ALIGN,
            align: SLNO_TEXT_ALIGN,
            flex: FlexForColumns.DESCRIPTION,
            cellClassName: "number-cell",
            hideable: false,
          },
        ]
      : []),
    {
      headerName: `${companyLabel}`,
      field: `${companyLabel}`,
      headerAlign: HEADER_TEXT_ALIGN,
      align: SLNO_TEXT_ALIGN,
      flex: FlexForColumns.NAME,
      cellClassName: "number-cell",
    },
    {
      headerName: `${subsidiaryLabel}`,
      field: subsidiaryLabel,
      headerAlign: HEADER_TEXT_ALIGN,
      align: SLNO_TEXT_ALIGN,
      flex: FlexForColumns.NAME,
      cellClassName: "number-cell",
    },
    {
      headerName: `${subsidiarySiteLabel}`,
      field: subsidiarySiteLabel,
      headerAlign: HEADER_TEXT_ALIGN,
      align: SLNO_TEXT_ALIGN,
      flex: FlexForColumns.NAME,
      cellClassName: "number-cell",
    },
    {
      headerName: `${junctionLabel}`,
      field: junctionLabel,
      align: SLNO_TEXT_ALIGN,
      flex: FlexForColumns.NAME,
      cellClassName: "number-cell",
    },
    {
      headerName: "Status",
      field: StockEntryTable.Status,
      headerAlign: HEADER_TEXT_ALIGN,
      flex: FlexForColumns.DESCRIPTION,
      cellClassName: "number-cell",
      renderCell: (params: any) => {
        return (
          <>
            <img
              src={
                items &&
                items.length &&
                items[0].node.ticketStatus === TicketStatus.CLOSED
                  ? Working
                  : Notworking
              }
              alt=""
            />
            {items &&
            items.length &&
            items[0].node.ticketStatus === TicketStatus.CLOSED
              ? "Working"
              : "Not-Working"}
          </>
        );
      },
    },
    {
      headerName: "Action",
      field: "Action",
      headerAlign: HEADER_TEXT_ALIGN,
      align: SLNO_TEXT_ALIGN,
      flex: FlexForColumns.NAME,
      cellClassName: "number-cell",
      renderCell: (params: any) => {
        return (
          <>
            <button
              className="particular-asset__asset-list--block--table--view"
              onClick={() => {
                dispatch({
                  type: payloadTypes.SET_ITEM_DETAILS_ID,
                  payload: {
                    itemDetailsId: params.row.itemDetailId,
                  },
                });
                setAssetDetails(!assetDetails);
              }}
            >
              View Details&nbsp;
              <img src={eye} alt="/" />
            </button>
          </>
        );
      },
    },
  ];

  const rows: GridValidRowModel[] = useMemo(() => {
    return items.map(({ node: data }, index) => {
      return {
        id: index + 1,
        serial_no: data.prodSerialNo,
        [`${companyLabel}`]: data.currentCompany.name,
        [`${subsidiaryLabel}`]: data.currentSubsidiary.name,
        [`${subsidiarySiteLabel}`]: data.currentSite.name,
        [`${junctionLabel}`]: data.currentJunction.name,
        itemDetailId: data.id,
        ticketStatus: data.ticketStatus,
      };
    });
  }, [items]);

  useEffect(() => {
    if (data && !loading) {
      const newData = data.GetItemsDetails.edges;
      console.log(newData);
      if (endCursor) {
        const updatedNewData = newData.map((newCompany) => {
          const filteredStudent = items.find(
            (sub) => sub.node.id === newCompany.node.id
          );
          if (filteredStudent) {
            return {
              ...newCompany,
              node: {
                ...newCompany.node,
              },
            };
          }
          return newCompany;
        });
        setItems(updatedNewData);
      } else {
        setItems(newData);
      }
      setEndCursor(data.GetItemsDetails.pageInfo.endCursor);
      setHasNextPage(data.GetItemsDetails.pageInfo.hasNextPage);
    } // eslint-disable-next-line
  }, [data, loading]);

  const handleScroll = () => {
    if (hasNextPage && !loading) {
      fetchMore({
        variables: {
          first: FETCH_MORE_DATA,
          after: endCursor,
        },
        updateQuery: (prevResult, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prevResult;

          const newEdges = fetchMoreResult.GetItemsDetails.edges;
          const pageInfo = fetchMoreResult.GetItemsDetails.pageInfo;
          setEndCursor(pageInfo.endCursor);
          setHasNextPage(pageInfo.hasNextPage);

          const duplicateCheck = prevResult.GetItemsDetails.edges.filter(
            ({ node: { id } }) =>
              newEdges.findIndex(({ node: { id: newId } }) => newId === id) !==
              -1
          );

          if (duplicateCheck.length > 0) return prevResult;

          return {
            GetItemsDetails: {
              edges: [...items, ...newEdges],
              pageInfo,
              totalCount: data ? data.GetItemsDetails.totalCount! : 0,
            },
          };
        },
      });
    }
  };

  const [columnVisibilityModel, setColumnVisibilityModel] =
    React.useState<GridColumnVisibilityModel>({
      id: true,
      serial_no: true,
      companyLabel: false,
      subsidiaryLabel: false,
      subsidiarySiteLabel: false,
      junctionLabel: true,
      actions: true,
    });

  useEffect(() => {
    const savedModel = localStorage.getItem(
      ColumnVisibilityFor.REPORTS_ASSET_DETAILS
    );
    if (savedModel) {
      setColumnVisibilityModel(JSON.parse(savedModel));
    }
  }, []);

  const handleColumnVisibilityChange = (
    newModel: GridColumnVisibilityModel
  ) => {
    setColumnVisibilityModel(newModel);
    localStorage.setItem(
      ColumnVisibilityFor.REPORTS_ASSET_DETAILS,
      JSON.stringify(newModel)
    );
  };

  const clearFilters = () => {
    dispatch({
      type: payloadTypes.SET_COMPANY_ID,
      payload: {
        companyId: 0,
      },
    });
    dispatch({
      type: payloadTypes.SET_SUB_ID,
      payload: {
        subsidiaryId: 0,
      },
    });
    dispatch({
      type: payloadTypes.SET_SUB_SITE_ID,
      payload: {
        subSiteId: 0,
      },
    });
    dispatch({
      type: payloadTypes.SET_JUNCTION_ID,
      payload: {
        junctionID: 0,
      },
    });
    setSearchData("");
  };

  useEffect(() => {
    clearFilters();
  }, []);

  return (
    <>
      <div className="particular-asset__asset-list">
        <div className="particular-asset__asset-list--filters row g-0">
          <div className="col-2">
            <Input
              id="data_search"
              placeholder="Search..."
              onChange={(e) => {
                setSearchData(e.target.value);
              }}
            />
          </div>

          <div className="col-2">
            <CompaniesAutocomplete
              onChange={(newValue) => {
                if (newValue) {
                  dispatch({
                    type: payloadTypes.SET_COMPANY_ID,
                    payload: {
                      companyId: newValue.node.id,
                    },
                  });
                } else {
                  dispatch({
                    type: payloadTypes.SET_COMPANY_ID,
                    payload: {
                      companyId: 0,
                    },
                  });
                }
              }}
              onKeyDown={emptyFunc}
              label={companyLabel}
              value={state.companyId ? state.companyId : 0}
            />
          </div>

          <div className="col-2">
            <SubsidiarySelect
              onChange={(newValue) => {
                if (newValue) {
                  dispatch({
                    type: payloadTypes.SET_SUB_ID,
                    payload: {
                      subsidiaryId: newValue.node.id,
                    },
                  });
                } else {
                  dispatch({
                    type: payloadTypes.SET_SUB_ID,
                    payload: {
                      subsidiaryId: 0,
                    },
                  });
                  dispatch({
                    type: payloadTypes.SET_SUB_SITE_ID,
                    payload: {
                      subSiteId: 0,
                    },
                  });
                  dispatch({
                    type: payloadTypes.SET_JUNCTION_ID,
                    payload: {
                      junctionID: 0,
                    },
                  });
                }
              }}
              label={subsidiaryLabel}
              companyID={state.companyId ? state.companyId : 0}
              onKeyDown={emptyFunc}
              value={state.subsidiaryId ? state.subsidiaryId : 0}
            />
          </div>

          <div className="col-2">
            <SiteSelect
              onChange={(newValue) => {
                if (newValue) {
                  dispatch({
                    type: payloadTypes.SET_SUB_SITE_ID,
                    payload: {
                      subSiteId: newValue.value,
                    },
                  });
                } else {
                  dispatch({
                    type: payloadTypes.SET_SUB_SITE_ID,
                    payload: {
                      subSiteId: 0,
                    },
                  });
                  dispatch({
                    type: payloadTypes.SET_JUNCTION_ID,
                    payload: {
                      junctionID: 0,
                    },
                  });
                }
              }}
              companyId={state.companyId ? state.companyId : 0}
              subsidiaryId={state.subsidiaryId ? state.subsidiaryId : 0}
              onKeyDown={emptyFunc}
              value={state.subSiteId ? state.subSiteId : 0}
              label={subsidiarySiteLabel}
            />
          </div>

          <div className="col-2">
            <JunctionsAutocomplete
              onChange={(newValue) => {
                if (newValue) {
                  dispatch({
                    type: payloadTypes.SET_JUNCTION_ID,
                    payload: {
                      junctionID: newValue.node.id,
                    },
                  });
                } else {
                  dispatch({
                    type: payloadTypes.SET_JUNCTION_ID,
                    payload: {
                      junctionID: 0,
                    },
                  });
                }
              }}
              companyID={state.companyId ? state.companyId : 0}
              subsidiaryID={state.subsidiaryId ? state.subsidiaryId : 0}
              siteID={state.subSiteId ? state.subSiteId : 0}
              onKeyDown={emptyFunc}
              value={state.junctionID ? state.junctionID : 0}
              label={junctionLabel}
            />
          </div>
        </div>
        <div className="particular-asset__asset-list--block row g-0">
          <div className="col-8 particular-asset__asset-list--block--table">
            {loadingState ? (
              <div className="loading-container">
                <img src={Loading} className="loading-gif" />
              </div>
            ) : (
              <StyledDatagrid
                columns={columns}
                rows={rows}
                rowHeight={TABLE_ROW_HEIGHT}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={handleColumnVisibilityChange}
                initialState={{
                  columns: {
                    columnVisibilityModel: columnVisibilityModel,
                  },
                }}
                disableRowSelectionOnClick
                hideFooter
                onRowsScrollEnd={() => {
                  if (data) {
                    handleScroll();
                  }
                }}
              />
            )}
          </div>

          <div className="col">
            <TrafficSignalsChart />
          </div>
        </div>
        <div className="row g-0">
          <div className="col-2">
            <Button mode="cancel" onClick={() => setDetailAssets(false)} />
          </div>
          <div className="col flex-end">
            <div className="overall-total-count">
              Total Assets <b>{data?.GetItemsDetails.totalCount}</b>
            </div>
          </div>
        </div>
      </div>

      <CustomDrawer
        anchor="right"
        open={assetDetails}
        onClose={() => setAssetDetails(!assetDetails)}
      >
        <AssetDetails setAssetDetails={setAssetDetails} />
      </CustomDrawer>
    </>
  );
};

export default AssetsList;
